import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuChevronLeft, LuChevronRight, LuColumns3, LuSearchX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { GLASS_CHIP, GLASS_PRESSABLE } from '../../../components/ui/glass';
import useHorizontalScroll from '../../../hooks/useHorizontalScroll';
import { useTranslation } from '../../../i18n/LanguageContext';
import { EMPLOYEE_COLUMNS, PINNED_COLUMNS, columnLabel } from '../employeeColumns';
import ColumnFilterButton from './ColumnFilterButton';
import EmployeeCell, { getCellTitle } from './EmployeeCell';

// Never page fewer rows than this, however short the screen
const MIN_ROWS = 3;
const DEFAULT_ROW_HEIGHT = 48;

// Round glass arrow over the table edge (ICON_BUTTON is `relative`, these are absolute)
const SCROLL_BUTTON = `tw-absolute tw-top-1/2 tw-z-10 tw-grid tw-h-10 tw-w-10 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-text-slate-600 dark:tw-text-slate-200 ${GLASS_CHIP} ${GLASS_PRESSABLE}`;

// min-w-0 / py-0 cancel the global `th` rule in style/TableFilter.css
const HEAD_CELL = [
  'tw-group tw-sticky tw-top-0 tw-h-11 tw-min-w-0 tw-whitespace-nowrap tw-border-b tw-border-slate-200/80 tw-bg-slate-50 tw-px-3 tw-py-0 tw-text-left tw-align-middle',
  'tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500',
  'dark:tw-border-white/10 dark:tw-bg-[#282c35] dark:tw-text-slate-400',
].join(' ');

const BODY_CELL = 'tw-h-12 tw-whitespace-nowrap tw-border-b tw-border-slate-100 tw-px-3 tw-align-middle tw-text-slate-700 tw-transition-colors tw-duration-200 dark:tw-border-white/[0.06] dark:tw-text-slate-200';

// Every cell is opaque: the pinned columns scroll over the others.
const rowBackground = (selected, flashing) => {
  if (flashing) return 'tw-bg-amber-100 dark:tw-bg-[#433d2d]';
  if (selected) return 'tw-bg-teal-50 dark:tw-bg-[#20383d]';
  return 'tw-bg-white group-hover:tw-bg-slate-50 dark:tw-bg-[#22262f] dark:group-hover:tw-bg-[#292e38]';
};

// The last pinned column casts a shadow while the table is scrolled sideways
const EDGE_SHADOW = "after:tw-pointer-events-none after:tw-absolute after:tw-inset-y-0 after:-tw-right-4 after:tw-w-4 after:tw-bg-gradient-to-r after:tw-from-slate-900/[0.08] after:tw-to-transparent after:tw-transition-opacity dark:after:tw-from-black/40";

/**
 * The employee table. It is as tall as its rows, so a short result ends
 * right under the last row. With `maxCardHeight` (the most its card may take)
 * it reports how many rows fit in that card (`onCapacityChange`), so a full
 * page never needs vertical scrolling either.
 * Ad/Soyad stay pinned while it scrolls sideways; columns resize from the
 * header edge (double-click resets); every header has a filter; rows are
 * keyboard-selectable (Enter/Space selects, ↑/↓ move).
 */
const EmployeeTable = ({
  rows,
  visibleKeys,
  getColumnWidth,
  onResizeColumn,
  onResetColumnWidth,
  filters,
  onFilterChange,
  selectedId,
  flashId,
  onSelect,
  pendingLeaveCounts,
  isLoading,
  isBusy,
  skeletonRows = 8,
  maxCardHeight,
  onCapacityChange,
  canClearFilters,
  onClearFilters,
  onShowColumns,
}) => {
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);
  const { canScrollLeft, canScrollRight, viewportWidth, scrollByPage } = useHorizontalScroll(scrollRef);
  const { t } = useTranslation();
  const [resizingKey, setResizingKey] = useState(null);

  const totalWidth = visibleKeys.reduce((sum, key) => sum + getColumnWidth(key), 0);
  const showSkeleton = isLoading && rows.length === 0;

  // Leading identity columns (Ad, Soyad) stay in view while scrolling sideways
  const pinnedKeys = [];
  for (let i = 0; i < visibleKeys.length && PINNED_COLUMNS.includes(visibleKeys[i]); i += 1) pinnedKeys.push(visibleKeys[i]);
  const pinnedOffsets = {};
  let pinnedWidth = 0;
  pinnedKeys.forEach((key) => {
    pinnedOffsets[key] = pinnedWidth;
    pinnedWidth += getColumnWidth(key);
  });
  // Only where they leave at least half of the width to the rest (not on phones)
  const pinActive = viewportWidth > 0 && pinnedKeys.length > 0 && pinnedWidth <= viewportWidth * 0.5;
  const lastPinnedKey = pinnedKeys[pinnedKeys.length - 1];
  const isPinned = (key) => pinActive && key in pinnedOffsets;
  const pinnedStyle = (key) => (isPinned(key) ? { left: pinnedOffsets[key] } : undefined);
  const pinnedClass = (key) => {
    if (!isPinned(key)) return '';
    if (key !== lastPinnedKey) return 'tw-sticky';
    return `tw-sticky ${EDGE_SHADOW} ${canScrollLeft ? 'after:tw-opacity-100' : 'after:tw-opacity-0'}`;
  };

  // Rows that fit when the card is at its tallest. Worked out from the card's
  // other parts (toolbar, footer, header row), not from the rows on screen, so
  // a short result doesn't shrink the page size for the next one.
  const measureCapacity = useCallback(() => {
    const wrapper = wrapperRef.current;
    const container = scrollRef.current;
    if (!wrapper || !container || !onCapacityChange || !maxCardHeight) return;
    const card = wrapper.parentElement;
    const cardChrome = card.offsetHeight - wrapper.offsetHeight; // toolbar, footer, card border
    const tableChrome = wrapper.offsetHeight - container.clientHeight; // top border, sideways scrollbar
    const head = container.querySelector('thead');
    const row = container.querySelector('tbody tr[data-row]');
    const rowHeight = row ? row.getBoundingClientRect().height : DEFAULT_ROW_HEIGHT;
    const available = maxCardHeight - cardChrome - tableChrome - (head ? head.getBoundingClientRect().height : 0) - 1;
    if (rowHeight > 0) onCapacityChange(Math.max(MIN_ROWS, Math.floor(available / rowHeight)));
  }, [onCapacityChange, maxCardHeight]);

  useLayoutEffect(() => {
    measureCapacity();
  });

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    // The card too: a wrapping toolbar or filter chips change the room left
    const observer = new ResizeObserver(() => measureCapacity());
    observer.observe(wrapper);
    if (wrapper.parentElement) observer.observe(wrapper.parentElement);
    return () => observer.disconnect();
  }, [measureCapacity]);

  const startResize = (event, key) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = handle.closest('th').getBoundingClientRect().width;
    const { cursor, userSelect } = document.body.style;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    handle.setPointerCapture(event.pointerId);
    setResizingKey(key);

    const handleMove = (moveEvent) => onResizeColumn(key, startWidth + moveEvent.clientX - startX);
    const handleEnd = () => {
      handle.removeEventListener('pointermove', handleMove);
      handle.removeEventListener('pointerup', handleEnd);
      handle.removeEventListener('pointercancel', handleEnd);
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
      setResizingKey(null);
    };
    handle.addEventListener('pointermove', handleMove);
    handle.addEventListener('pointerup', handleEnd);
    handle.addEventListener('pointercancel', handleEnd);
  };

  const handleRowKeyDown = (event, employee) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(employee);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const sibling = event.key === 'ArrowDown' ? event.currentTarget.nextElementSibling : event.currentTarget.previousElementSibling;
      if (sibling) sibling.focus();
    }
  };

  const renderEmpty = () => (
    <tr className="tw-bg-transparent">
      <td colSpan={visibleKeys.length + 1} className="tw-border-0 tw-p-0">
        <div className="tw-sticky tw-left-0 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-14 tw-text-center" style={{ width: viewportWidth || undefined }}>
          <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
            {visibleKeys.length === 0
              ? <LuColumns3 aria-hidden="true" className="tw-h-7 tw-w-7" />
              : <LuSearchX aria-hidden="true" className="tw-h-7 tw-w-7" />}
          </span>
          <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">
            {visibleKeys.length === 0 ? t('employees.table.noColumns') : t('employees.table.empty')}
          </p>
          {visibleKeys.length === 0 ? (
            <Button size="sm" onClick={onShowColumns}>{t('employees.table.showDefaultColumns')}</Button>
          ) : canClearFilters && (
            <Button size="sm" onClick={onClearFilters}>{t('employees.table.clearFilters')}</Button>
          )}
        </div>
      </td>
    </tr>
  );

  const renderSkeleton = () => Array.from({ length: skeletonRows }, (_, index) => (
    <tr key={index} data-row className="tw-bg-transparent">
      {visibleKeys.map((key, column) => (
        <td
          key={key}
          style={pinnedStyle(key)}
          className={`${BODY_CELL} tw-bg-white dark:tw-bg-[#22262f] ${isPinned(key) ? 'tw-sticky tw-z-[1]' : ''}`}
        >
          <span
            className="tw-block tw-h-3 tw-animate-pulse tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10"
            style={{ width: `${45 + ((index * 7 + column * 13) % 40)}%` }}
          />
        </td>
      ))}
      <td aria-hidden="true" className={`${BODY_CELL} tw-bg-white dark:tw-bg-[#22262f]`} />
    </tr>
  ));

  const renderRows = () => rows.map((employee) => {
    const selected = employee.id === selectedId;
    const flashing = employee.id === flashId;
    const background = rowBackground(selected, flashing);
    return (
      <tr
        key={employee.id}
        data-row
        data-employee-id={employee.id}
        tabIndex={0}
        aria-selected={selected}
        onClick={() => onSelect(employee)}
        onKeyDown={(event) => handleRowKeyDown(event, employee)}
        className="tw-group tw-cursor-pointer tw-bg-transparent focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:-tw-outline-offset-2 focus-visible:tw-outline-teal-500/70"
      >
        {visibleKeys.map((key, column) => (
          <td
            key={key}
            title={getCellTitle(key, employee)}
            style={pinnedStyle(key)}
            className={`${BODY_CELL} ${background} ${isPinned(key) ? `tw-z-[1] ${pinnedClass(key)}` : 'tw-overflow-hidden'} ${
              column === 0 && selected ? 'tw-shadow-[inset_3px_0_0_#14b8a6]' : ''
            }`}
          >
            <EmployeeCell columnKey={key} employee={employee} pendingLeaveCount={pendingLeaveCounts[employee.id] || 0} />
          </td>
        ))}
        <td aria-hidden="true" className={`${BODY_CELL} ${background}`} />
      </tr>
    );
  });

  let body;
  if (showSkeleton) body = renderSkeleton();
  else if (rows.length === 0 || visibleKeys.length === 0) body = renderEmpty();
  else body = renderRows();

  return (
    // Shrinks with min-h-0 (and the table then scrolls) only if more rows are
    // shown than fit, e.g. before the first measurement
    <div ref={wrapperRef} className="tw-relative tw-flex tw-min-h-0 tw-flex-col tw-border-t tw-border-slate-200/70 dark:tw-border-white/10">
      <div ref={scrollRef} className="tw-min-h-0 tw-overflow-auto glass-scrollbar">
        <table
          aria-label={t('employees.table.label')}
          aria-busy={isLoading}
          className="tw-border-separate tw-border-spacing-0 tw-text-sm"
          style={{ tableLayout: 'fixed', width: `max(100%, ${totalWidth}px)` }}
        >
          <colgroup>
            {visibleKeys.map((key) => <col key={key} style={{ width: getColumnWidth(key) }} />)}
            {/* Takes up the space left over when the columns are narrower than the table */}
            <col />
          </colgroup>
          <thead>
            <tr className="tw-bg-transparent">
              {visibleKeys.map((key) => {
                const config = EMPLOYEE_COLUMNS[key];
                const label = columnLabel(t, key);
                return (
                  <th
                    key={key}
                    scope="col"
                    data-column={key}
                    style={pinnedStyle(key)}
                    className={`${HEAD_CELL} ${isPinned(key) ? `tw-z-[3] ${pinnedClass(key)}` : 'tw-z-[2]'}`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-pr-2">
                      <span className="tw-min-w-0 tw-truncate" title={label}>{label}</span>
                      <ColumnFilterButton columnKey={key} column={config} value={filters[key]} onChange={onFilterChange} />
                    </div>
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={t('employees.table.resizeLabel', { label })}
                      title={t('employees.table.resizeHint')}
                      onPointerDown={(event) => startResize(event, key)}
                      onDoubleClick={() => onResetColumnWidth(key)}
                      className="tw-absolute tw-right-0 tw-top-0 tw-z-[1] tw-flex tw-h-full tw-w-3 tw-cursor-col-resize tw-touch-none tw-justify-end"
                    >
                      <span
                        className={`tw-my-auto tw-h-5 tw-w-[3px] tw-rounded-full tw-transition-colors ${
                          resizingKey === key
                            ? 'tw-bg-teal-500'
                            : 'tw-bg-transparent group-hover:tw-bg-slate-300 dark:group-hover:tw-bg-white/20'
                        }`}
                      />
                    </span>
                  </th>
                );
              })}
              <th aria-hidden="true" className={`${HEAD_CELL} tw-z-[2]`} />
            </tr>
          </thead>
          <tbody className={`tw-transition-opacity tw-duration-200 ${isBusy ? 'tw-opacity-60' : 'tw-opacity-100'}`}>{body}</tbody>
        </table>
      </div>

      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollByPage(-1)}
          aria-label={t('employees.table.scrollLeft')}
          style={{ left: (pinActive ? pinnedWidth : 0) + 12 }}
          className={SCROLL_BUTTON}
        >
          <LuChevronLeft aria-hidden="true" className="tw-h-5 tw-w-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollByPage(1)}
          aria-label={t('employees.table.scrollRight')}
          className={`${SCROLL_BUTTON} tw-right-4`}
        >
          <LuChevronRight aria-hidden="true" className="tw-h-5 tw-w-5" />
        </button>
      )}
    </div>
  );
};

EmployeeTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  visibleKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  getColumnWidth: PropTypes.func.isRequired,
  onResizeColumn: PropTypes.func.isRequired,
  onResetColumnWidth: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  flashId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  pendingLeaveCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  isLoading: PropTypes.bool,
  isBusy: PropTypes.bool,
  skeletonRows: PropTypes.number,
  maxCardHeight: PropTypes.number,
  onCapacityChange: PropTypes.func,
  canClearFilters: PropTypes.bool,
  onClearFilters: PropTypes.func.isRequired,
  onShowColumns: PropTypes.func.isRequired,
};

export default EmployeeTable;
